import React from "react";
import { Icon } from "semantic-ui-react";

class ConnectionIndicator extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      signalStrength: 0, // 0-5 bars
      rtt: 0,
      packetLoss: 0,
      connectionState: "unknown",
    };
    this.statsInterval = null;
    this.lastByteReceived = 0;
    this.lastTimestamp = 0;
  }

  componentDidMount() {
    // Check stats every 1000ms
    this.statsInterval = setInterval(() => {
      this.updateConnectionStats();
    }, 1000);
  }

  componentWillUnmount() {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
    }
  }

  updateConnectionStats = async () => {
    try {
      if (!this.props.stream || !this.props.stream.getTracks) {
        return;
      }

      const tracks = this.props.stream.getTracks();
      if (tracks.length === 0) {
        this.setState({ connectionState: "disconnected", signalStrength: 0 });
        return;
      }

      const audioTrack = tracks.find(t => t.kind === "audio");
      
      if (audioTrack) {
        const trackState = audioTrack.readyState;
        let strength = 5;

        if (trackState === "live") {
          let packetLoss = 0;
          let rtt = 0;

          // Try peer connection if provided
          if (this.props.peerConnection && this.props.peerConnection.getStats) {
            try {
              const stats = await this.props.peerConnection.getStats();
              let totalPacketsLost = 0;
              let totalPacketsReceived = 0;
              let lastByteReceived = this.lastByteReceived || 0;
              let lastTimestamp = this.lastTimestamp || 0;

              stats.forEach((report) => {
                if (report.type === "inbound-rtp" && report.kind === "audio") {
                  totalPacketsLost = report.packetsLost || 0;
                  totalPacketsReceived = report.packetsReceived || 0;
                  const bytesReceived = report.bytesReceived || 0;
                  const timestamp = report.timestamp;

                  if (totalPacketsReceived > 0) {
                    packetLoss = (totalPacketsLost / (totalPacketsLost + totalPacketsReceived)) * 100;
                  }

                  this.setState({ 
                    packetLoss: packetLoss.toFixed(2),
                  });
                }
                
                if (report.type === "candidate-pair" && report.state === "succeeded") {
                  rtt = report.currentRoundTripTime
                    ? report.currentRoundTripTime * 1000
                    : 0;
                  this.setState({
                    rtt: rtt.toFixed(0),
                  });
                }
              });

              strength = this.calculateSignalStrength(packetLoss, rtt);
            } catch (error) {
              console.log("Error getting WebRTC stats:", error);
              strength = 4;
            }
          }

          this.setState({
            connectionState: "connected",
            signalStrength: strength,
          });
        } else if (trackState === "ended") {
          this.setState({ connectionState: "disconnected", signalStrength: 0 });
        } else {
          this.setState({ connectionState: "connecting", signalStrength: 2 });
        }
      } else {
        this.setState({ connectionState: "unknown", signalStrength: 0 });
      }
    } catch (error) {
      console.log("Error updating connection stats:", error);
    }
  };

  calculateSignalStrength = (packetLoss, rtt) => {
    // Calculate signal strength (0-5 bars) based on connection metrics
    let strength = 5;

    // Packet loss is critical - even 1% is bad
    if (packetLoss > 5) {
      strength = 1;
    } else if (packetLoss > 2) {
      strength = 2;
    } else if (packetLoss > 0.5) {
      strength = 3;
    }

    // RTT (round trip time) - higher is worse
    if (rtt > 500) {
      strength = Math.min(strength, 2);
    } else if (rtt > 250) {
      strength = Math.min(strength, 3);
    } else if (rtt > 100) {
      strength = Math.min(strength, 4);
    }

    return Math.max(0, Math.min(5, strength));
  };

  getConnectionStatus = () => {
    const { connectionState, signalStrength } = this.state;

    if (connectionState === "disconnected") {
      return { color: "#d32f2f", title: "No Connection" };
    } else if (connectionState === "connecting") {
      return { color: "#fbc02d", title: "Connecting..." };
    } else if (signalStrength <= 1) {
      return { color: "#d32f2f", title: "Poor Connection" };
    } else if (signalStrength === 2) {
      return { color: "#ff9800", title: "Fair Connection" };
    } else if (signalStrength === 3) {
      return { color: "#fbc02d", title: "Good Connection" };
    } else if (signalStrength >= 4) {
      return { color: "#4caf50", title: "Excellent Connection" };
    }
    return { color: "#999", title: "Unknown" };
  };

  render() {
    const status = this.getConnectionStatus();

    return (
      <Icon
        name="wifi"
        size="small"
        style={{
          color: status.color,
          cursor: "pointer",
          transition: "color 0.3s ease",
          margin: 0,
          flexShrink: 0,
        }}
        title={status.title}
      />
    );
  }
}

export default ConnectionIndicator;
